import React, { useCallback, useEffect, useState } from "react";
import api from "../lib/api";
import Header from "../components/app/Header";
import { toast } from "sonner";
import ContactDetailPanel from "../components/app/ContactDetailPanel";
import FieldFillPill from "../components/app/FieldFillPill";
import FieldFillCounter from "../components/app/FieldFillCounter";
import { AlertTriangle, Loader2, MessageSquare, Ban, RefreshCw, Award, Quote } from "lucide-react";

const FILTERS = ["open", "recovered", "responded_outside", "dismissed"];

function inr(n) {
  const v = Number(n || 0);
  return "₹" + v.toLocaleString("en-IN");
}

export default function MissedMoney() {
  const [filter, setFilter] = useState("open");
  const [cases, setCases] = useState(null);
  const [summary, setSummary] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [openContact, setOpenContact] = useState(null);
  const [conn, setConn] = useState(null);
  const [lastScan, setLastScan] = useState(null);

  const load = useCallback(async () => {
    try {
      const [l, s] = await Promise.all([
        api.get(`/missed-money/list?status=${filter}`),
        api.get(`/missed-money/summary`),
      ]);
      setCases(l.data?.cases || []);
      setLastScan(l.data?.last_scan);
      setSummary(s.data);
    } catch (e) { setCases([]); setSummary({}); }
  }, [filter]);

  useEffect(() => {
    load();
    api.get("/employz/connection").then((r) => setConn(r.data)).catch(() => setConn({ status: "not_connected" }));
  }, [load]);

  const rescan = async () => {
    setScanning(true);
    try {
      await api.post(`/missed-money/scan`);
      toast.success("Scan complete.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Scan failed."); }
    finally { setScanning(false); }
  };

  const dismiss = async (id) => {
    const reason = window.prompt("Dismiss reason? (not_a_lead / handled_offline / other)", "other");
    if (!reason) return;
    try { await api.post(`/missed-money/${id}/dismiss`, { reason }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Dismiss failed."); }
  };

  const openRecover = async (c) => {
    try {
      const r = await api.get(`/employz/contacts-table`);
      const contact = (r.data?.contacts || []).find((x) => x.ghl_contact_id === c.ghl_contact_id);
      if (contact) setOpenContact({ ...contact, _missed_money_case_id: c.id, _missed_money_delay_days: c.days_unanswered });
      else toast.error("Contact not found — sync may be stale.");
    } catch (e) { toast.error("Could not open contact."); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-7xl mx-auto px-6 md:px-10 pt-8 pb-16">
        <div className="mb-6">
          <div className="text-[10px] uppercase tracking-[0.28em] text-emerald-400">// Employz Intelligence / Missed Money Finder</div>
          <h1 className="mt-2 font-display text-4xl md:text-5xl tracking-tighter font-black text-white flex items-center gap-3">
            <AlertTriangle className="w-9 h-9 text-rose-400" />
            Missed Money Finder
          </h1>
        </div>

        {summary && (
          <div className="rounded-2xl border border-rose-500/20 bg-gradient-to-br from-rose-950/20 via-zinc-950 to-zinc-950 p-6 mb-6" data-testid="missed-money-headline">
            <div className="text-2xl md:text-3xl text-white font-display font-black tracking-tight">
              <span className="text-rose-300" data-testid="missed-money-open-count">{summary.open_count || 0}</span> customers were ready to buy. Nobody replied.
            </div>
            <div className="mt-2 text-xl text-emerald-300 font-display font-black">
              Estimated value: <span data-testid="missed-money-value">{inr(summary.headline_value)}</span>
            </div>
            {(summary.mentioned_count || 0) > 0 ? (
              <div className="text-[12px] text-emerald-400/90 mt-1" data-testid="missed-money-mentioned-breakdown">
                Includes <b>{inr(summary.mentioned_total)}</b> mentioned directly by {summary.mentioned_count} customer{summary.mentioned_count === 1 ? "" : "s"} in their reply.
                {(summary.opportunity_total || summary.estimate_total) ? (
                  <span className="text-zinc-500"> Rest from open deals + your typical deal size.</span>
                ) : null}
              </div>
            ) : (
              <div className="text-[11px] text-zinc-500 mt-1">(based on open deals + your typical deal size)</div>
            )}
            <div className="mt-4 flex items-center gap-3">
              {lastScan?.scanned_at && (
                <div className="text-[11px] text-zinc-500">Last scan: {new Date(lastScan.scanned_at).toLocaleString()}</div>
              )}
              <button onClick={rescan} disabled={scanning} data-testid="missed-money-rescan" className="ml-auto inline-flex items-center gap-1 text-[11px] uppercase tracking-[0.18em] px-2.5 py-1.5 border border-zinc-800 text-zinc-300 rounded">
                {scanning ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />} Rescan
              </button>
            </div>
            <div className="mt-3">
              <FieldFillCounter testid="missed-money-field-fill-counter" />
            </div>
          </div>
        )}

        <div className="flex items-center gap-1 mb-4">
          {FILTERS.map((f) => (
            <button key={f} onClick={() => setFilter(f)} data-testid={`mm-filter-${f}`}
              className={`text-[11px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-full border ${filter === f ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300" : "border-zinc-800 text-zinc-500"}`}>
              {f.replace(/_/g, " ")}
            </button>
          ))}
        </div>

        {cases === null ? (
          <div className="text-sm text-zinc-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading…</div>
        ) : cases.length === 0 ? (
          <div className="rounded-lg border border-dashed border-zinc-800 py-10 text-center text-sm text-zinc-500">
            No cases in this filter.
          </div>
        ) : (
          <div className="space-y-4" data-testid="missed-money-cases">
            {cases.map((c) => (
              <div key={c.id} data-testid={`missed-money-case-${c.id}`} className={`rounded-xl border p-5 ${c.status === "responded_outside" ? "border-zinc-900 bg-zinc-900/20 opacity-70" : c.strength === "strong" ? "border-rose-500/30 bg-rose-950/10" : "border-zinc-900 bg-zinc-950/60"}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-white font-medium">{c.contact_name}</span>
                    {c.band && <span className={`text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border ${c.band === "hot" ? "border-rose-500/40 text-rose-300" : c.band === "warm" ? "border-amber-500/40 text-amber-300" : "border-zinc-700 text-zinc-400"}`}>{c.band}</span>}
                    {c.strength === "strong" && <span className="text-[9px] uppercase tracking-[0.18em] px-1.5 py-0.5 rounded border border-rose-500/40 text-rose-300">strong intent</span>}
                  </div>
                  {c.value !== null && c.value !== undefined && (
                    <span className="text-emerald-300 font-mono text-sm" data-testid={`mm-value-${c.id}`}>
                      {c.value_source === "mentioned" ? (
                        <span title={c.mentioned_amount_text ? `Customer said: "${c.mentioned_amount_text}"` : undefined}>
                          <span className="text-[9px] uppercase tracking-[0.18em] text-emerald-400 border border-emerald-500/40 rounded px-1 py-0.5 mr-1.5">said</span>
                          {inr(c.value)}
                        </span>
                      ) : c.value_source === "estimate" ? (
                        `est. ${inr(c.value)}`
                      ) : (
                        inr(c.value)
                      )}
                    </span>
                  )}
                </div>
                <div className="mt-3 rounded-lg bg-zinc-950 border border-zinc-900 p-3 relative">
                  <Quote className="w-3 h-3 text-zinc-700 absolute top-2 left-2" />
                  <div className="text-base text-white italic pl-5 pr-2 whitespace-pre-wrap" data-testid={`mm-quote-${c.id}`}>{c.quote || c.message_text}</div>
                </div>
                <div className="mt-2 text-[11px] text-zinc-500">no reply · {c.days_unanswered} days ago</div>
                <FieldFillPill ghlContactId={c.ghl_contact_id} testid={`mm-fills-${c.id}`} />
                {c.status === "responded_outside" && (
                  <div className="mt-2 text-[11px] text-amber-300">Responded outside CRM (per your notes) — worth confirming</div>
                )}
                {c.status === "open" && (
                  <div className="mt-3 flex items-center gap-2">
                    <button onClick={() => openRecover(c)} data-testid={`mm-recover-${c.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded"><MessageSquare className="w-3 h-3" /> Recover</button>
                    <button onClick={() => dismiss(c.id)} data-testid={`mm-dismiss-${c.id}`} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] px-2 py-1.5 border border-zinc-800 text-zinc-500 rounded"><Ban className="w-3 h-3" /> Dismiss</button>
                  </div>
                )}
                {c.status === "recovered" && (
                  <div className="mt-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-emerald-300"><Award className="w-3 h-3" /> Recovered</div>
                )}
              </div>
            ))}
          </div>
        )}

        {openContact && conn && (
          <ContactDetailPanel
            contact={openContact}
            connection={conn}
            onClose={async () => {
              const cid = openContact._missed_money_case_id;
              setOpenContact(null);
              if (cid) {
                try { await api.post(`/missed-money/${cid}/mark-recovered`); toast.success("Case marked recovered."); load(); }
                catch (_e) { /* silent */ }
              }
            }}
            onScoreUpdated={load}
          />
        )}
      </main>
    </div>
  );
}
